import React from 'react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@core-ui/Components/ui/table.tsx';
import { Skeleton } from '@core-ui/Components/ui/skeleton.tsx';
import { Pencil, Trash2 } from 'lucide-react';
import { IconPhoneCheck, IconPhoneOff } from '@tabler/icons-react';
import { cn } from '@core-ui-lib/utils.ts';
import { EmptyState } from '@ui/shared/emptyState.tsx';
import { type NumberPool } from '@types';
import { useTranslation } from 'react-i18next';

function TableSkeleton ({ rows = 10 }: { rows?: number }): React.JSX.Element {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <TableRow key={`s-${i}`} className="border-b border-border/60 hover:bg-transparent">
          <TableCell className="py-3">
            <div className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-full bg-muted-foreground/20" />
              <Skeleton className="h-4 w-40 rounded bg-muted-foreground/20" />
            </div>
          </TableCell>
          <TableCell className="py-3">
            <Skeleton className="h-4 w-56 rounded bg-muted-foreground/20" />
          </TableCell>
          <TableCell className="py-3">
            <div className="flex gap-2">
              <Skeleton className="h-5 w-14 rounded-full bg-muted-foreground/20" />
              <Skeleton className="h-5 w-14 rounded-full bg-muted-foreground/20" />
            </div>
          </TableCell>
          <TableCell className="py-3">
            <Skeleton className="h-4 w-40 rounded bg-muted-foreground/20" />
          </TableCell>
          <TableCell className="py-3">
            <Skeleton className="h-4 w-12 rounded bg-muted-foreground/20" />
          </TableCell>
          <TableCell className="py-3">
            <Skeleton className="h-6 w-16 rounded-full bg-muted-foreground/20" />
          </TableCell>
          <TableCell className="py-3">
            <div className="flex justify-center gap-3">
              <Skeleton className="h-8 w-8 rounded-full bg-muted-foreground/20" />
              <Skeleton className="h-8 w-8 rounded-full bg-muted-foreground/20" />
            </div>
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

interface Props {
  loading: boolean;
  numbers: NumberPool[];
  onEdit: (n: NumberPool) => void;
  onAskDelete: (id: string | null) => void;
  skeletonRows?: number;
  isSuperAdmin: boolean;
  isCallerId: boolean;
}

export default function NumbersTable ({
  loading,
  numbers,
  onEdit,
  onAskDelete,
  skeletonRows = 10,
  isSuperAdmin,
  isCallerId,
}: Props): React.JSX.Element {
  const { t } = useTranslation();

  function renderNumberType (type: string): string  {
    switch (type) {
    case 'ORGANIZATION': return t('adminConsole.numberDialog.typeOrganization', { defaultValue: type });
    case 'DEFAULT': return  t('adminConsole.numberDialog.typeDefault', { defaultValue: type });
    default: return type ?? '—';
    }
  }

  return (
    <div className="w-full overflow-auto rounded-md border border-border bg-card" data-qa-id={isCallerId ? 'callerId-table' : 'numbers-table'}>
      <Table>
        <TableHeader className="bg-muted/60">
          <TableRow className="border-b border-border/60">
            <TableHead>{t('adminConsole.numbersTable.number', { defaultValue: 'Number' })}</TableHead>
            {!isCallerId && <TableHead>{t('adminConsole.numbersTable.location', { defaultValue: 'Location' })}</TableHead>  }
            {!isCallerId && <TableHead>{t('adminConsole.numbersTable.owner', { defaultValue: 'Owner' })}</TableHead> }
            <TableHead>{t('adminConsole.numbersTable.timesUsed', { defaultValue: 'Times used' })}</TableHead>
            {!isCallerId && <TableHead>{t('adminConsole.numbersTable.numberType', { defaultValue: 'Number type' })}</TableHead> }
            <TableHead>{t('adminConsole.numbersTable.status', { defaultValue: 'Status' })}</TableHead>
            <TableHead className="text-center">
              {t('adminConsole.numbersTable.actions', { defaultValue: 'Actions' })}
            </TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {loading ? (
            <TableSkeleton rows={skeletonRows} />
          ) : numbers.length === 0 ? (
            <tr>
              <td colSpan={7} className="py-10" data-qa-id={isCallerId ? 'callerId-not-found' : 'number-not-found' }>
                <EmptyState message={t('adminConsole.numbersTable.numNotFound', { defaultValue: 'Numbers not found.' })} />
              </td>
            </tr>
          ) : (
            numbers.map((number, index) => (
              <TableRow
                data-qa-id={`number-row-${index}`}
                key={number.id}
                className={cn(
                  'border-b border-border hover:bg-muted/30',
                  number.deletedAt != null && 'opacity-50'
                )}
              >
                <TableCell data-qa-id={`number-cell-${index}`} className="text-sm text-muted-foreground">{number.number}</TableCell>
                { !isCallerId && <TableCell data-qa-id={`location-cell-${index}`} className="text-sm">{number.location ?? '—'}</TableCell>}
                { !isCallerId && <TableCell data-qa-id={`owner-cell-${index}`} className="text-sm">{number.ownerName ?? '—'}</TableCell>}
                <TableCell className="text-sm">{number.timesUsed ?? '—'}</TableCell>
                { !isCallerId &&
                  <TableCell data-qa-id={`number-type-cell-${index}`} className="text-sm">
                    {renderNumberType(number.type)}
                  </TableCell>
                }
                <TableCell className="text-sm">
                  {number.active === true
                    ? <IconPhoneCheck data-qa-id="number-active" className="text-[var(--phone-check)]" />
                    : <IconPhoneOff data-qa-id="number-inactive" className="text-error" />}
                </TableCell>

                {number.type !== 'DEFAULT' ? (
                  <TableCell className="text-center">
                    <div className="flex justify-center gap-3">

                      {(isSuperAdmin || (number.type === 'ORGANIZATION' || number.type === 'USER')) &&
                        <button
                          data-qa-id={isCallerId ? 'edit-callerId-button' : 'edit-number-button' }
                          onClick={() => {
                            onEdit(number);
                          }}
                          disabled={number.deletedAt != null}
                          className="cursor-pointer"
                          aria-label={t('adminConsole.numbersTable.editNumber', { defaultValue: 'Edit number' })}
                          title={t('adminConsole.numbersTable.editNumber', { defaultValue: 'Edit number' })}
                        >
                          <Pencil className="w-4 h-4 text-sky-500 hover:text-sky-800" />
                        </button>
                      }
                      <button
                        data-qa-id={isCallerId ? 'delete-callerId-button' : 'delete-number-button'}
                        onClick={() => {
                          onAskDelete(number.id);
                        }}
                        disabled={number.deletedAt != null}
                        className="cursor-pointer"
                        aria-label={t('adminConsole.numbersTable.deleteNumber', { defaultValue: 'Delete number' })}
                        title={t('adminConsole.numbersTable.deleteNumber', { defaultValue: 'Delete number' })}
                      >
                        <Trash2 className="w-4 h-4 text-red-600 hover:text-red-800" />
                      </button>
                    </div>
                  </TableCell>) : (
                  <TableCell className="text-center">
                    <div className="flex justify-center gap-3">
                    </div>
                  </TableCell>
                )
                }
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}